import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

const Root = styled.span`
  font-family: 'Montserrat', Tahoma, Geneva, Verdana, sans-serif;
  color: ${props => {
    return props.color
      ? props.theme.colors[props.color]
      : props.theme.colors.copy;
  }};
  margin: 0;
  text-align: ${props => props.align};
`;

const H1 = styled(Root)`
  font-size: ${props => props.theme.sizes.h1Size};
  letter-spacing: -2%;
  line-height: 54px;
  font-weight: 800;
`;
const H2 = styled(Root)`
  font-size: ${props => props.theme.sizes.h2Size};
  letter-spacing: -2%;
  line-height: 62px;
  font-weight: 700;
`;
const H3 = styled(Root)`
  font-size: ${props => props.theme.sizes.h3Size};
  letter-spacing: -0.5px;
  line-height: 125%;
  font-weight: 700;
`;
const H4 = styled(Root)`
  font-size: ${props => props.theme.sizes.h4Size};
  letter-spacing: -0.5px;
  line-height: 36px;
  font-weight: 700;
`;
const H5 = styled(Root)`
  font-size: ${props => props.theme.sizes.h5Size};
  letter-spacing: -0.5px;
  line-height: auto;
  font-weight: 700;
`;
const H6 = styled(Root)`
  font-size: ${props => props.theme.sizes.h6Size};
  letter-spacing: -0.5px;
  line-height: auto;
  font-weight: 700;
`;
const BodyLarge = styled(Root)`
  font-size: ${props => props.theme.sizes.bodyLargeSize};
  letter-spacing: -3%;
  line-height: 150%;
`;
const BodyMedium = styled(Root)`
  font-size: ${props => props.theme.sizes.bodyMediumSize};
  letter-spacing: -3%;
  line-height: 139%;
`;
const BodySmall = styled(Root)`
  font-size: ${props => props.theme.sizes.bodySmallSize};
  letter-spacing: -1%;
  line-height: auto;
`;
const CaptionLarge = styled(Root)`
  font-size: ${props => props.theme.sizes.captionLargeSize};
  letter-spacing: 0px;
  line-height: auto;
`;
const CaptionSmall = styled(Root)`
  font-size: ${props => props.theme.sizes.captionSmallSize};
  letter-spacing: 1%;
  line-height: auto;
`;
const Tiny = styled(Root)`
  font-size: ${props => props.theme.sizes.tinySize};
  letter-spacing: 0%;
  line-height: auto;
`;

const defaultVariantMapping = {
  h1: { as: 'h1', Component: H1 },
  h2: { as: 'h2', Component: H2 },
  h3: { as: 'h3', Component: H3 },
  h4: { as: 'h4', Component: H4 },
  h5: { as: 'h5', Component: H5 },
  h6: { as: 'h6', Component: H6 },
  bodyLarge: { as: 'p', Component: BodyLarge },
  bodyMedium: { as: 'p', Component: BodyMedium },
  bodySmall: { as: 'p', Component: BodySmall },
  captionLarge: { as: 'p', Component: CaptionLarge },
  captionSmall: { as: 'p', Component: CaptionSmall },
  tiny: { as: 'p', Component: Tiny },
};

export const Typography = ({
  className,
  variant,
  children: text,
  align = 'left',
  color = 'white',
  ...other
}) => {
  const { Component, as } = defaultVariantMapping[variant] || {
    as: 'p',
    Component: BodyMedium,
  };

  return (
    <Component
      className={className}
      align={align}
      as={as}
      color={color}
      {...other}
    >
      {text}
    </Component>
  );
};

Typography.propTypes = {
  variant: PropTypes.oneOfType([
    PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'bodyLarge',
      'bodyMedium',
      'bodySmall',
      'captionLarge',
      'captionSmall',
      'tinySize',
    ]),
    PropTypes.string,
  ]),
  align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf([
    'copy',
    'copyDimmed',
    'brand',
    'complimentaryTeal',
    'complimentaryBlue',
    'complimentaryCerise',
    'complimentaryPurple',
    'complimentaryYellow',
    'systemSuccess',
    'systemWarning',
    'systemError',
  ]),
};

//** USAGE */
// <Typography variant="h1">TEST H1</Typography>
// <Typography variant="h2">TEST H2</Typography>
// <Typography variant="h3">TEST H3</Typography>
// <Typography variant="bodyMedium">TEST BODY1</Typography>
// <Typography variant="captionSmall">TEST SUBTITLE1</Typography>
